import React, { useState } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { validateField } from "./ui-components/utils";
import { toast } from "react-toastify";
import {getTenantById , updateTenant, createAuth0User, getRolesByTenantId} from "./api/tenantApi";
import { getRoleRecordId } from "./utils/rolePermissions";

function AddUserModal({ open, onClose, tenantInfo }) {
  const [userData, setUserData] = useState({ email: "", password: "", name: "", roleId: "" });
  const [errors, setErrors] = useState({
    email: "",
    password: "",
    name: "",
    roleId: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState("");

  React.useEffect(() => {
    if (open && tenantInfo?.id) {
      setRolesLoading(true);
      setRolesError("");
      getRolesByTenantId(tenantInfo.id)
        .then((res) => {
          setRoles(res?.data || []);
        })
        .catch((err) => {
          setRolesError("Failed to load roles");
          setRoles([]);
        })
        .finally(() => setRolesLoading(false));
    }
    if (!open) {
      setRoles([]);
      setRolesError("");
    }
  }, [open, tenantInfo?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const validations = {
      password: [
        { type: "Required", validationMessage: "Password is required" },
        {
          type: "Regex",
          regex:
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/,
          validationMessage:
            "Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, one digit, and one special character from (!, @, #, $, %, ^, &, *, .)",
        },
      ],
      email: [
        { type: "Required", validationMessage: "Email is required" },
        {
          type: "Email",
          validationMessage: "Please enter a valid email address",
        },
      ],
      name: [
        { type: "Required", validationMessage: "Name is required" }
      ]
    };
    let hasErrors = false;
    const newErrors = { email: "", password: "", name: "", roleId: "" };

    for (const field in validations) {
      const validationResult = validateField(
        userData[field],
        validations[field]
      );
      if (validationResult.hasError) {
        newErrors[field] = validationResult.errorMessage;
        hasErrors = true;
      }
    }
    setErrors(newErrors);

    if (!hasErrors) {
      setIsLoading(true);
      try {
        // 1. Create user in Auth0
        const roleId = userData.roleId === "" || userData.roleId === "default" ? null : userData.roleId;
        const response = await createAuth0User({
          email: userData.email,
          user_metadata: { tenantId: tenantInfo.id, roleId: roleId },
          app_metadata: { tenantId: tenantInfo.id, roleId: roleId },
          name: userData.name,
          connection: "Username-Password-Authentication",
          password: userData.password,
          verify_email: false,
        });

        // 4. Update tenant email list as before
        let tenantFromDB = await getTenantById(tenantInfo.id);
        const tenantDBData = tenantFromDB?.data || {};
        if (!tenantDBData?.emailList?.includes(userData?.email)) {
          const input = {
            id: tenantInfo.id,
            emailList: tenantDBData?.emailList
              ? [...tenantDBData.emailList, userData.email]
              : [userData.email],
          };
          await updateTenant(tenantInfo.id, input);
        }

        toast.success("User Added Successfully!");
        handleClose();
      } catch (e) {
        const d = e?.response?.data;
        const raw = d?.details?.message || d?.details?.error || d?.message || d?.error || e?.message || "";
        const isConflict = d?.details?.statusCode === 409 || e?.response?.status === 409 || /already exists/i.test(raw);
        toast.warn(
          isConflict
            ? "A user with this email already exists. Use “Invite” to (re)send them a set-password link, or add a different email."
            : (raw || "Something went wrong")
        );
        console.error("Add user failed:", d || e);
      } finally {
        setIsLoading(false);
      }
    } else {
      console.error("Form has errors. Please fix them.");
    }
  };
  const handleClose = () => {
    setUserData({
      email: "",
      password: "",
      name: "",
      roleId: ""
    });
    setErrors({
      email: "",
      password: "",
      name: "",
      roleId: ""
    });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby='modal-title'
      aria-describedby='modal-description'>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            handleClose();
          }
        }}>
        <Box
          sx={{
            width: 400,
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
          }}>
          <Typography id='modal-title' variant='h6' component='h2' gutterBottom>
            Enter User Data
          </Typography>
          <TextField
            label='Name'
            variant='outlined'
            name='name'
            margin='normal'
            error={!!errors.name}
            helperText={errors.name}
            value={userData.name}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            label='Email'
            variant='outlined'
            name='email'
            margin='normal'
            error={!!errors.email}
            helperText={errors.email}
            value={userData.email}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            label='Password'
            variant='outlined'
            name='password'
            error={!!errors.password}
            value={userData.password}
            onChange={handleChange}
            helperText={errors.password}
            fullWidth
          />
          <TextField
            select
            label='Role'
            name='roleId'
            value={userData.roleId}
            onChange={handleChange}
            error={!!errors.roleId}
            helperText={errors.roleId || rolesError}
            margin='normal'
            fullWidth
            SelectProps={{ native: true }}
            disabled={rolesLoading}
          >
            <option value='' disabled>
              {rolesLoading ? 'Loading roles...' : 'Select a role'}
            </option>
            {roles.length === 0 && !rolesLoading && (
              <option value='default'>
                Default User
              </option>
            )}
            {roles.map((role) => {
              const id = getRoleRecordId(role);
              return (
                <option key={id} value={id}>
                  {role.name}
                </option>
              );
            })}
          </TextField>
          <Button
            onClick={handleSubmit}
            variant='contained'
            sx={{ mt: 2 }}
            disabled={isLoading}>
            {isLoading ? "Loading" : "Save"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

export default AddUserModal;